import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type {
    Commerce,
    CommerceCartPurchaseType,
    CommerceEnrollmentKit,
    CommerceEnrollmentSession,
    CommerceImage,
    CommerceProduct,
    CommerceProductCategory,
    CommerceProductSort,
    CommerceProductSummary,
} from "@teqtank/commerce-sdk";
import "../checkout/teqtank-checkout.js";
import {
    enrollmentStageRoutesConverter,
    isCurrentBrowserRoute,
    resolveEnrollmentStageRoute,
    type EnrollmentStageRoutes,
} from "./enrollment-stage-routes.js";
import { sanitizeCommerceHtml } from "../products/sanitize-commerce-html.js";

@customElement("teqtank-enrollment")
export class TeqtankEnrollment extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "stage-routes", converter: enrollmentStageRoutesConverter })
    public accessor stageRoutes: EnrollmentStageRoutes = Object.freeze({});
    @property({ attribute: "checkout-route" }) public accessor checkoutRoute = "";
    @property({ attribute: "disable-stage-scroll", type: Boolean })
    public accessor disableStageScroll = false;
    @state() private accessor enrollment: CommerceEnrollmentSession | undefined;
    @state() private accessor products: readonly CommerceProductSummary[] = [];
    @state() private accessor categories: readonly CommerceProductCategory[] = [];
    @state() private accessor query = "";
    @state() private accessor categoryId = "";
    @state() private accessor sort: CommerceProductSort = "default";
    @state() private accessor busy = false;
    @state() private accessor loadingProducts = false;
    @state() private accessor error = "";
    @state() private accessor awaitingCheckoutContinuation = false;
    @state() private accessor detailProduct: CommerceProduct | undefined;
    @state() private accessor detailSummary: CommerceProductSummary | undefined;
    @state() private accessor detailLoading = false;
    @state() private accessor detailError = "";
    @state() private accessor detailActiveImageIndex = 0;
    @query(".product-dialog") private accessor productDialog!: HTMLDialogElement;
    private unsubscribe: (() => void) | undefined;
    private initializedCommerce: Commerce | undefined;
    private searchTimer: ReturnType<typeof setTimeout> | undefined;
    private requestedRouteKey = "";
    private detailImagePointerStartX: number | undefined;

    static override styles = css`
        :host {
            container-type: inline-size;
            display: block;
            scroll-margin-block-start: var(--teqtank-enrollment-scroll-offset, 6rem);
            color: var(--teqtank-color-text, #13231d);
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        * {
            box-sizing: border-box;
        }
        button,
        input,
        select {
            font: inherit;
        }
        .shell {
            overflow: hidden;
            border: 1px solid #e2e9e4;
            border-radius: var(
                --teqtank-enrollment-card-radius,
                var(--teqtank-radius-large, 1.65rem)
            );
            background: var(
                --teqtank-enrollment-card-background,
                var(--teqtank-color-surface, #fff)
            );
            box-shadow: var(--teqtank-shadow-large, 0 1.6rem 5rem rgba(17, 48, 36, 0.11));
        }
        .hero {
            position: relative;
            display: grid;
            grid-template-columns: minmax(0, 1.35fr) minmax(15rem, 0.65fr);
            gap: 2rem;
            padding: clamp(1.6rem, 4cqw, 3.8rem);
            color: var(--teqtank-enrollment-hero-color, #fff);
            background: var(
                --teqtank-enrollment-hero-background,
                linear-gradient(
                    120deg,
                    var(--stage-accent, var(--teqtank-color-primary, #075f55)),
                    #102f29 72%,
                    #173c34
                )
            );
        }
        .hero:after {
            position: absolute;
            inset: 0;
            content: "";
            pointer-events: none;
            background: radial-gradient(
                circle at 82% 10%,
                rgba(255, 255, 255, 0.22),
                transparent 29%
            );
        }
        .hero-copy,
        .progress {
            position: relative;
            z-index: 1;
        }
        .hero-copy {
            align-self: center;
        }
        .eyebrow {
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.15em;
            text-transform: uppercase;
            opacity: 0.82;
        }
        h1 {
            max-width: 46rem;
            margin: 0.55rem 0 0.8rem;
            font-family: var(--teqtank-font-heading, Georgia, serif);
            font-size: var(--teqtank-font-size-heading, clamp(2rem, 4.7cqw, 3.75rem));
            line-height: var(--teqtank-line-height-tight, 1);
            letter-spacing: -0.045em;
        }
        .intro {
            max-width: 44rem;
            margin: 0;
            color: rgba(255, 255, 255, 0.78);
            font-size: 1.03rem;
            line-height: 1.65;
        }
        .progress {
            align-self: end;
            padding: 1.1rem;
            border: 1px solid rgba(255, 255, 255, 0.22);
            border-radius: 1rem;
            background: rgba(255, 255, 255, 0.09);
            backdrop-filter: blur(12px);
        }
        .progress-row {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            font-size: 0.78rem;
            font-weight: 850;
        }
        .track {
            height: 0.42rem;
            margin-top: 0.75rem;
            overflow: hidden;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.17);
        }
        .track span {
            display: block;
            height: 100%;
            border-radius: inherit;
            background: #fff;
        }
        .body {
            padding: clamp(1.2rem, 3.4cqw, 3rem);
        }
        .notice {
            margin-bottom: 1rem;
            padding: 0.9rem 1rem;
            border-radius: 0.85rem;
            background: #fff0ed;
            color: #8c2d20;
        }
        .loading {
            display: grid;
            place-items: center;
            min-height: 22rem;
            border: 1px solid #e3e9e5;
            border-radius: 1.4rem;
            background: linear-gradient(140deg, #f7fbf8, #fff);
            color: #53645c;
            font-weight: 800;
        }
        .tools {
            display: grid;
            grid-template-columns: minmax(13rem, 1fr) auto;
            gap: 0.75rem;
            margin-bottom: 1rem;
        }
        .search,
        .sort {
            min-height: 3rem;
            border: 1px solid #d9e2dc;
            border-radius: 0.8rem;
            background: #fff;
            color: inherit;
        }
        .search {
            padding: 0.75rem 1rem;
        }
        .sort {
            padding: 0.65rem 2.3rem 0.65rem 0.85rem;
        }
        .categories {
            display: flex;
            gap: 0.55rem;
            overflow: auto;
            margin: 0 0 1.15rem;
            padding: 0.05rem 0 0.4rem;
            scrollbar-width: thin;
        }
        .chip {
            flex: none;
            min-height: 2.35rem;
            padding: 0.5rem 0.8rem;
            border: 1px solid #dce5df;
            border-radius: 999px;
            background: #fff;
            color: #46574f;
            font-size: 0.8rem;
            font-weight: 800;
            cursor: pointer;
        }
        .chip.active {
            border-color: var(--teqtank-color-primary, #075f55);
            background: #eaf6f1;
            color: #075f55;
        }
        .kit-grid,
        .product-grid {
            display: grid;
            gap: var(--teqtank-enrollment-grid-gap, 1rem);
        }
        .kit-grid {
            grid-template-columns: var(
                --teqtank-enrollment-option-columns,
                var(--teqtank-enrollment-grid-columns, repeat(3, minmax(0, 1fr)))
            );
        }
        .product-grid {
            grid-template-columns: var(
                --teqtank-enrollment-grid-columns,
                repeat(2, minmax(0, 1fr))
            );
        }
        .kit,
        .product {
            position: relative;
            min-width: 0;
            overflow: hidden;
            border: 1px solid #e2e8e4;
            border-radius: var(
                --teqtank-enrollment-card-radius,
                var(--teqtank-radius-medium, 1.2rem)
            );
            background: var(
                --teqtank-enrollment-card-background,
                linear-gradient(145deg, #fff 0%, #fbfdfc 64%, #f2f8f5 100%)
            );
            box-shadow: 0 0.35rem 1.25rem rgba(20, 50, 39, 0.07);
            transition:
                transform 0.2s,
                border-color 0.2s,
                box-shadow 0.2s;
        }
        .kit {
            display: grid;
            grid-template-rows:
                var(--teqtank-enrollment-option-media-height, 13rem)
                minmax(0, 1fr);
            min-height: 26rem;
            padding: 0;
            text-align: left;
            color: inherit;
            cursor: pointer;
        }
        .kit:hover,
        .kit:focus-visible,
        .product:hover {
            transform: translateY(-4px);
            border-color: var(--teqtank-color-primary, #075f55);
            box-shadow: 0 1rem 2.4rem rgba(19, 56, 42, 0.14);
            outline: 0;
        }
        .product:has(.product-detail-trigger:focus-visible) {
            transform: translateY(-4px);
            border-color: var(--teqtank-color-primary, #075f55);
            box-shadow:
                0 0 0 3px color-mix(in srgb, var(--teqtank-color-primary, #075f55) 18%, transparent),
                0 1rem 2.4rem rgba(19, 56, 42, 0.14);
        }
        .kit.selected {
            border-color: var(--teqtank-color-primary, #075f55);
            box-shadow:
                0 0 0 3px color-mix(in srgb, var(--teqtank-color-primary, #075f55) 13%, transparent),
                0 1rem 2.4rem rgba(19, 56, 42, 0.12);
        }
        .kit-copy,
        .product-copy {
            position: relative;
            z-index: 1;
            display: flex;
            min-width: 0;
            flex-direction: column;
            align-items: flex-start;
        }
        .kit-copy {
            padding: 1.15rem;
        }
        .badge {
            display: inline-flex;
            max-width: 100%;
            padding: 0.28rem 0.58rem;
            border-radius: 999px;
            background: #eaf3ff;
            color: #2464a0;
            font-size: 0.64rem;
            font-weight: 900;
            letter-spacing: 0.1em;
            text-transform: uppercase;
        }
        .kit-media .badge {
            position: absolute;
            z-index: 1;
            top: 0.75rem;
            left: 0.75rem;
        }
        .kit h2,
        .product h2 {
            margin: 0.65rem 0 0.38rem;
            font-size: clamp(1.05rem, 2cqw, 1.35rem);
            line-height: 1.08;
            letter-spacing: -0.02em;
        }
        .kit h2 {
            margin-top: 0;
        }
        .kit p,
        .product p {
            display: -webkit-box;
            margin: 0;
            color: #68756e;
            font-size: 0.82rem;
            line-height: 1.4;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
        }
        .price {
            margin-top: auto;
            padding-top: 1rem;
            font-size: 1.3rem;
            font-weight: 950;
            letter-spacing: -0.025em;
        }
        .kit .price {
            width: 100%;
            margin-top: 0.9rem;
            padding-top: 0.9rem;
            border-top: 1px solid #e2e8e4;
        }
        .kit-media,
        .product-media {
            display: grid;
            place-items: center;
            min-width: 0;
        }
        .kit-media {
            position: relative;
            box-sizing: border-box;
            width: 100%;
            height: var(--teqtank-enrollment-option-media-height, 13rem);
            min-height: 0;
            overflow: hidden;
            padding: 1rem;
            background: radial-gradient(circle at 50% 50%, #fff, #f1f7f4);
        }
        .kit-media img,
        .product-media img {
            width: 100%;
            max-height: 11rem;
            object-fit: contain;
            filter: drop-shadow(0 0.75rem 0.8rem rgba(17, 38, 29, 0.18));
        }
        .kit-media img {
            position: absolute;
            inset: 1rem;
            display: block;
            width: calc(100% - 2rem);
            height: calc(100% - 2rem);
            max-height: none;
            object-position: center;
        }
        .placeholder {
            display: grid;
            width: 100%;
            aspect-ratio: 1;
            place-items: center;
            border-radius: 50%;
            background: radial-gradient(circle, #e8f5ef, #f8fbf9 70%);
            color: #8ca198;
            font-size: 2rem;
        }
        .product {
            display: grid;
            grid-template-rows: clamp(13rem, 24cqw, 16rem) minmax(0, 1fr);
            border-color: color-mix(in srgb, var(--teqtank-color-primary, #075f55) 14%, #e2e8e4);
            border-radius: var(
                --teqtank-enrollment-product-radius,
                var(--teqtank-enrollment-card-radius, 1.15rem)
            );
            background: #fff;
            box-shadow: 0 0.65rem 1.8rem rgba(20, 50, 39, 0.09);
        }
        .product-media {
            overflow: hidden;
            padding: 1.35rem 1.35rem 1.75rem;
            border-bottom: 1px solid #e5ebe7;
            background: linear-gradient(145deg, #fff 0%, #f1f7f4 100%);
        }
        .product-media img {
            width: min(100%, 15rem);
            height: calc(100% - 0.5rem);
            max-height: none;
            object-fit: contain;
            object-position: center;
        }
        .product-copy {
            height: 100%;
            padding: 1.15rem;
        }
        .product-detail-trigger {
            position: absolute;
            z-index: 1;
            inset: 0;
            border: 0;
            border-radius: inherit;
            background: transparent;
            cursor: pointer;
        }
        .product-detail-trigger:focus-visible {
            outline: 0;
        }
        .product-media,
        .product-copy {
            pointer-events: none;
        }
        .product-main {
            width: 100%;
        }
        .product h2 {
            margin-top: 0.8rem;
            font-size: clamp(1.15rem, 2.2cqw, 1.45rem);
        }
        .product p {
            -webkit-line-clamp: 2;
        }
        .product-footer {
            display: grid;
            width: 100%;
            gap: 0.7rem;
            margin-top: auto;
            padding-top: 1rem;
        }
        .product .price {
            width: 100%;
            margin-top: 0;
            padding-top: 0.85rem;
            border-top: 1px solid #e5ebe7;
            color: var(--teqtank-color-text, #13231d);
            font-size: 1.4rem;
        }
        .product-action {
            position: relative;
            z-index: 2;
            width: 100%;
            min-width: 0;
            min-height: 2.85rem;
            margin-top: 0;
            padding: 0.7rem 1rem;
            border: 0;
            border-radius: 999px;
            background: var(--teqtank-color-primary, #075f55);
            color: #fff;
            font-weight: 900;
            line-height: 1.25;
            overflow-wrap: anywhere;
            white-space: normal;
            cursor: pointer;
            pointer-events: auto;
            box-shadow: 0 0.45rem 1rem
                color-mix(in srgb, var(--teqtank-color-primary, #075f55) 20%, transparent);
        }
        .product-action:hover:not(:disabled),
        .product-action:focus-visible {
            filter: brightness(0.94);
            outline: 3px solid
                color-mix(in srgb, var(--teqtank-color-primary, #075f55) 22%, transparent);
            outline-offset: 2px;
        }
        .product-added {
            width: 100%;
            margin-top: 0.7rem;
            color: var(--teqtank-color-primary, #075f55);
            font-size: 0.75rem;
            font-weight: 850;
        }
        .product-actions {
            display: grid;
            min-width: 0;
        }
        .stage-layout {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(17rem, 23rem);
            gap: 1.3rem;
        }
        .catalogue h2 {
            margin: 0 0 0.35rem;
            font-size: 1.45rem;
        }
        .catalogue-intro {
            margin: 0 0 1.2rem;
            color: #66756d;
            line-height: 1.55;
        }
        .summary {
            align-self: start;
            position: sticky;
            top: 1rem;
            padding: 1.15rem;
            border: 1px solid #dfe8e2;
            border-radius: 1rem;
            background: #f8fbf9;
        }
        .summary-continue {
            display: grid;
            gap: 0.65rem;
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #dfe8e2;
        }
        .summary-continue p {
            margin: 0;
            color: #617068;
            font-size: 0.78rem;
            line-height: 1.45;
        }
        .summary-continue .action {
            width: 100%;
            min-height: 3.2rem;
            box-shadow: 0 0.7rem 1.4rem
                color-mix(in srgb, var(--teqtank-color-primary, #075f55) 22%, transparent);
        }
        .line {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 0.7rem;
            padding: 0.68rem 0;
            border-bottom: 1px solid #e3e9e5;
        }
        .line:last-child {
            border: 0;
        }
        .lock {
            display: block;
            margin-top: 0.2rem;
            color: #617068;
            font-size: 0.72rem;
        }
        .actions {
            display: flex;
            justify-content: space-between;
            gap: 0.75rem;
            margin-top: 1.4rem;
            padding-top: 1.2rem;
            border-top: 1px solid #e1e7e3;
        }
        .action {
            min-height: 3.05rem;
            border: 0;
            border-radius: 999px;
            padding: 0.68rem 1.15rem;
            background: linear-gradient(
                135deg,
                var(--teqtank-color-primary, #075f55),
                color-mix(in srgb, var(--teqtank-color-primary, #075f55) 78%, #102f29)
            );
            color: #fff;
            font-weight: 900;
            cursor: pointer;
        }
        .secondary {
            border: 1px solid #d7e0da;
            background: #fff;
            color: var(--teqtank-color-text, #13231d);
        }
        .product-dialog {
            width: min(64rem, calc(100% - 2rem));
            max-height: min(48rem, calc(100dvh - 2rem));
            padding: 0;
            overflow: hidden;
            border: 0;
            border-radius: 1.4rem;
            background: var(--teqtank-color-surface, #fff);
            color: var(--teqtank-color-text, #13231d);
            box-shadow: 0 2rem 6rem rgba(10, 29, 22, 0.28);
        }
        .product-dialog::backdrop {
            background: rgba(7, 24, 18, 0.62);
            backdrop-filter: blur(4px);
        }
        .dialog-close {
            position: absolute;
            z-index: 2;
            top: 0.8rem;
            right: 0.8rem;
            display: grid;
            width: 2.75rem;
            height: 2.75rem;
            place-items: center;
            border: 1px solid rgba(19, 35, 29, 0.12);
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.94);
            color: inherit;
            font-size: 1.5rem;
            cursor: pointer;
        }
        .dialog-layout {
            display: grid;
            grid-template-columns: minmax(18rem, 0.9fr) minmax(0, 1.1fr);
            max-height: inherit;
            overflow: auto;
        }
        .dialog-gallery {
            position: sticky;
            top: 0;
            align-self: start;
            display: grid;
            gap: 0.75rem;
            padding: 2rem;
            background: linear-gradient(145deg, #fff, #edf5f1);
        }
        .dialog-media {
            position: relative;
            display: grid;
            min-height: 0;
            aspect-ratio: 1;
            place-items: center;
            overflow: hidden;
            border-radius: var(--teqtank-radius-large, 1.4rem);
            background: rgba(255, 255, 255, 0.62);
            touch-action: pan-y;
            user-select: none;
        }
        .dialog-media img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            filter: drop-shadow(0 1rem 1rem rgba(17, 38, 29, 0.16));
            -webkit-user-drag: none;
        }
        .dialog-thumbnails {
            display: flex;
            gap: 0.65rem;
            max-width: 100%;
            overflow-x: auto;
            padding: 0.1rem 0.1rem 0.3rem;
            scrollbar-width: thin;
            scroll-snap-type: inline proximity;
        }
        .dialog-thumbnail {
            flex: 0 0 4.5rem;
            aspect-ratio: 1;
            overflow: hidden;
            border: 1px solid rgba(19, 35, 29, 0.14);
            border-radius: 0.85rem;
            padding: 0;
            background: rgba(255, 255, 255, 0.78);
            opacity: 0.72;
            scroll-snap-align: start;
            cursor: pointer;
        }
        .dialog-thumbnail.active {
            border-color: var(--teqtank-color-accent, #075f55);
            box-shadow: 0 0 0 2px
                color-mix(in srgb, var(--teqtank-color-accent, #075f55) 22%, transparent);
            opacity: 1;
        }
        .dialog-thumbnail img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
        .dialog-mobile-gallery-controls {
            display: none;
            grid-template-columns: 2.75rem 1fr 2.75rem;
            align-items: center;
            gap: 0.75rem;
        }
        .dialog-gallery-navigation {
            display: grid;
            width: 2.75rem;
            height: 2.75rem;
            place-items: center;
            border: 1px solid rgba(19, 35, 29, 0.14);
            border-radius: 999px;
            background: #fff;
            color: inherit;
            font-size: 1.3rem;
            line-height: 1;
            cursor: pointer;
        }
        .dialog-gallery-position {
            color: #5e6d65;
            font-size: 0.8rem;
            font-weight: 800;
            text-align: center;
        }
        .dialog-copy {
            padding: clamp(1.5rem, 4cqw, 2.4rem);
        }
        .dialog-copy h2 {
            margin: 0.45rem 0 0.7rem;
            font-family: var(--teqtank-font-heading, Georgia, serif);
            font-size: clamp(1.8rem, 4cqw, 2.8rem);
            line-height: 1;
        }
        .dialog-description {
            color: #5e6d65;
            line-height: 1.65;
        }
        .dialog-detail-sections {
            display: grid;
            gap: 0.9rem;
            margin-top: 1.15rem;
        }
        .dialog-detail-section {
            color: #405048;
            line-height: 1.65;
        }
        .dialog-detail-section h3 {
            margin: 0 0 0.55rem;
            color: var(--teqtank-color-text, #13231d);
            font-size: 1rem;
        }
        .dialog-detail-section > :first-child,
        .dialog-detail-content > :first-child {
            margin-top: 0;
        }
        .dialog-detail-section > :last-child,
        .dialog-detail-content > :last-child {
            margin-bottom: 0;
        }
        .dialog-detail-section summary {
            color: var(--teqtank-color-text, #13231d);
            font-weight: 850;
            cursor: pointer;
        }
        .dialog-detail-section[open] summary {
            margin-bottom: 0.65rem;
        }
        button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        .empty {
            grid-column: 1/-1;
            padding: 2.2rem;
            border: 1px dashed #cedbd3;
            border-radius: 1rem;
            text-align: center;
            color: #627168;
        }
        .ineligible {
            display: grid;
            place-items: center;
            min-height: 20rem;
            text-align: center;
        }
        .ineligible div {
            max-width: 34rem;
        }
        .ineligible h2 {
            font-size: 1.8rem;
        }
        @container (max-width:900px) {
            .hero,
            .stage-layout {
                grid-template-columns: 1fr;
            }
            .summary {
                position: static;
            }
            .kit-grid {
                grid-template-columns: var(
                    --teqtank-enrollment-option-columns-medium,
                    repeat(2, minmax(0, 1fr))
                );
            }
            .product-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
        }
        @container (max-width:560px) {
            .hero {
                padding: 1.35rem;
            }
            .body {
                padding: 1rem;
            }
            .tools {
                grid-template-columns: 1fr;
            }
            .kit-grid {
                grid-template-columns: var(--teqtank-enrollment-option-columns-small, 1fr);
            }
            .product-grid {
                grid-template-columns: 1fr;
            }
            .kit {
                min-height: 24rem;
            }
            .actions {
                flex-direction: column-reverse;
            }
            .actions button {
                width: 100%;
            }
            .product-actions,
            .dialog-layout {
                grid-template-columns: 1fr;
            }
            .product-dialog {
                width: min(100% - 1rem, 34rem);
                max-height: calc(100dvh - 1rem);
                border-radius: 1rem;
            }
            .dialog-gallery {
                position: static;
                gap: 0.6rem;
                padding: 1rem;
            }
            .dialog-media {
                min-height: 0;
                aspect-ratio: 4 / 3;
                border-radius: 0.9rem;
            }
            .dialog-thumbnails {
                display: none;
            }
            .dialog-mobile-gallery-controls {
                display: grid;
            }
            .dialog-copy {
                padding: 1.25rem;
            }
        }
        .card-content {
            display: grid;
            gap: 0.25rem;
            margin: 0.65rem 0 0;
            color: #5e6d65;
            font-size: 0.75rem;
            line-height: 1.4;
        }
        .card-content-line {
            display: flex;
            flex-wrap: wrap;
            gap: 0.25rem;
        }
        .card-content-label {
            color: var(--teqtank-color-text, #13231d);
            font-weight: 850;
        }
        .card-content-rich {
            width: 100%;
        }
        .card-content-rich > :first-child {
            margin-top: 0;
        }
        .card-content-rich > :last-child {
            margin-bottom: 0;
        }
    `;

    override connectedCallback(): void {
        super.connectedCallback();
        this.bind();
    }
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) this.bind();
        if (changed.has("stageRoutes") || changed.has("checkoutRoute")) {
            this.requestedRouteKey = "";
            this.queueStageRouteRequest();
        }
    }
    override disconnectedCallback(): void {
        this.unsubscribe?.();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        super.disconnectedCallback();
    }

    protected override render() {
        if (!this.enrollment)
            return html`<div class="loading" part="loading-state" role="status">
                ${this.error || "Preparing enrollment options…"}
            </div>`;
        if (this.awaitingCheckoutContinuation) return this.renderCheckout();
        if (
            this.enrollment.currentStage.kind === "checkout" &&
            this.hasPendingExternalStageRoute()
        ) {
            const route = this.currentStageRoute();
            return html`<div class="loading" part="loading-state" role="status">
                Continuing to secure checkout…
                <a href=${route} @click=${this.requestCurrentStageRoute}>Continue</a>
            </div>`;
        }
        if (this.enrollment.currentStage.kind === "checkout") return this.renderCheckout();
        const stage = this.enrollment.currentStage;
        const accent = stage.presentation.accentColor ?? "var(--teqtank-color-primary,#075f55)";
        return html`<section class="shell" part="shell" style=${`--stage-accent:${accent}`}>
                <header class="hero" part="hero">
                    <div class="hero-copy" part="hero-content">
                        <div class="eyebrow" part="eyebrow">${stage.presentation.eyebrow}</div>
                        <h1 part="title">${stage.presentation.title}</h1>
                        <p class="intro" part="description">${stage.presentation.description}</p>
                    </div>
                    <div class="progress" part="progress">
                        <div class="progress-row" part="progress-label">
                            <span>${stage.progress.label}</span
                            ><span>${stage.progress.current}/${stage.progress.total}</span>
                        </div>
                        <div class="track" part="progress-track">
                            <span
                                part="progress-value"
                                style=${`width:${Math.max(0, Math.min(100, (stage.progress.current / stage.progress.total) * 100))}%`}
                            ></span>
                        </div>
                    </div>
                </header>
                <div class="body" part="body">
                    ${
                        this.error
                            ? html`<div class="notice" part="error-state" role="alert">
                                  ${this.error}
                              </div>`
                            : nothing
                    }${
                        !this.enrollment.eligibility.eligible
                            ? this.renderIneligible()
                            : this.renderStage()
                    }
                </div>
            </section>
            ${this.renderProductDialog()}`;
    }

    private renderIneligible() {
        return html`<div class="ineligible" part="ineligible-state">
            <div>
                <div class="eyebrow" part="eyebrow">Enrollment unavailable</div>
                <h2>
                    ${
                        this.enrollment?.eligibility.message ??
                        "This account cannot begin enrollment."
                    }
                </h2>
                <p>Use a different eligible account or return to the Shop.</p>
                <button class="action secondary" part="secondary-action" @click=${this.abandon}>
                    Return to Shop
                </button>
            </div>
        </div>`;
    }
    private renderCheckout() {
        return html`<teqtank-checkout
            part="checkout"
            exportparts="shell:checkout-shell, section:checkout-section, summary:checkout-summary, primary-action:primary-action, error-state:error-state"
            .commerce=${this.commerce}
            .enrollmentRoute=${this.currentStageRoute()}
            .enrollmentStageRoutes=${this.stageRoutes}
            @teqtank-checkout-continue-enrollment=${this.continueEnrollmentOnCurrentPage}
            @teqtank-order-submitted=${this.checkoutSubmitted}
            @teqtank-order-continuation-requested=${this.continueAfterCheckout}
        ></teqtank-checkout>`;
    }
    private renderStage() {
        const enrollment = this.enrollment!;
        if (enrollment.status === "completed" || enrollment.currentStage.kind === "complete") {
            const terminalStage = enrollment.currentStage.kind === "complete";
            return html`<div class="ineligible" part="complete-state">
                <div>
                    <div class="eyebrow" part="eyebrow">Enrollment confirmed</div>
                    <h2>
                        ${
                            terminalStage
                                ? enrollment.currentStage.presentation.title
                                : "Enrollment complete"
                        }
                    </h2>
                    <p>
                        ${
                            terminalStage
                                ? enrollment.currentStage.presentation.description
                                : "Your enrollment order is confirmed."
                        }
                    </p>
                </div>
            </div>`;
        }
        if (enrollment.currentStage.kind === "kit-selection") return this.renderKits();
        if (
            enrollment.currentStage.kind === "catalogue" ||
            enrollment.currentStage.kind === "recurring-offer"
        ) {
            return this.renderCatalogue();
        }
        return html`<div part="journey-stage">${this.renderActions()}</div>`;
    }
    private renderKits() {
        const kits = this.filteredKits();
        return html`${this.renderTools(true)}${this.renderCategoryChips(
                this.enrollment!.categories,
            )}
            <div class="kit-grid" part="option-list">
                ${
                    kits.length
                        ? kits.map((kit) => this.renderKit(kit))
                        : html`<div class="empty" part="empty-state">
                              No enrollment options match your search.
                          </div>`
                }
            </div>`;
    }
    private renderKit(kit: CommerceEnrollmentKit) {
        const offer = kit.product.representativeOffers[0];
        return html`<button
            class="kit ${this.enrollment?.selectedKitId === kit.id ? "selected" : ""}"
            part="option-card"
            ?disabled=${this.busy || !kit.available}
            @click=${() => this.selectKit(kit.id)}
        >
            <div class="kit-media" part="option-media">
                <span class="badge" part="badge">${kit.badge ?? "Enrollment option"}</span>${
                    kit.product.primaryImage
                        ? html`<img
                              src=${kit.product.primaryImage.url}
                              alt=${kit.product.primaryImage.alternativeText}
                          />`
                        : html`<div class="placeholder">✦</div>`
                }
            </div>
            <div class="kit-copy" part="option-content">
                <h2 part="option-title">${kit.product.name}</h2>
                <p part="option-description">${kit.product.shortDescription}</p>
                <div class="price" part="option-price">${offer?.price.current.formatted ?? ""}</div>
                ${this.renderCardContent(kit.product)}
            </div>
        </button>`;
    }
    private renderCatalogue() {
        const recurring = this.enrollment!.currentStage.kind === "recurring-offer";
        return html`<div class="stage-layout" part="stage-layout">
                <div class="catalogue" part="catalogue">
                    <h2 part="stage-title">
                        ${recurring ? "Build your recurring ritual" : "Add to your first order"}
                    </h2>
                    <p class="catalogue-intro" part="stage-description">
                        ${
                            recurring
                                ? "Choose only the products you want delivered again. Cadence and recurring pricing remain explicit."
                                : "Browse the same current Shop catalogue while your required enrollment items stay protected."
                        }
                    </p>
                    ${this.renderTools(false)}${this.renderCategoryChips(this.categories)}
                    <div class="product-grid" part="product-list">
                        ${
                            this.loadingProducts
                                ? html`<div class="empty" part="loading-state">
                                      Loading current products…
                                  </div>`
                                : this.products.length
                                  ? this.products.map((product) =>
                                        this.renderProduct(
                                            product,
                                            recurring ? "recurring" : "one-time",
                                        ),
                                    )
                                  : html`<div class="empty" part="empty-state">
                                        No available products match these filters.
                                    </div>`
                        }
                    </div>
                </div>
                ${this.renderCart()}
            </div>
            ${this.renderActions()}`;
    }
    private renderProduct(product: CommerceProductSummary, purchaseType: CommerceCartPurchaseType) {
        const offer = product.representativeOffers.find(
            (value) => value.purchaseType === purchaseType,
        );
        const selectedQuantity = this.enrollment!.cart.lines.filter(
            (value) =>
                value.line.productId === product.id && value.line.purchaseType === purchaseType,
        ).reduce((sum, value) => sum + value.line.quantity, 0);
        return html`<article class="product" part="product-card">
            <button
                class="product-detail-trigger"
                type="button"
                aria-label=${`View details for ${product.name}`}
                @click=${() => this.openProductDetails(product)}
            ></button>
            <div class="product-media" part="product-media">
                ${
                    product.primaryImage
                        ? html`<img
                              src=${product.primaryImage.url}
                              alt=${product.primaryImage.alternativeText}
                          />`
                        : html`<div class="placeholder">✦</div>`
                }
            </div>
            <div class="product-copy" part="product-content">
                <div class="product-main">
                    <span class="badge" part="badge"
                        >${
                            purchaseType === "recurring" ? "Recurring eligible" : "Optional add-on"
                        }</span
                    >
                    <h2 part="product-title">${product.name}</h2>
                    <p part="product-description">${product.shortDescription}</p>
                    ${this.renderCardContent(product)}
                </div>
                <div class="product-footer">
                    <div class="price" part="product-price">
                        ${
                            offer?.price.current.formatted ??
                            product.representativeOffers[0]?.price.current.formatted ??
                            ""
                        }
                    </div>
                    ${
                        selectedQuantity > 0
                            ? html`<div class="product-added" part="product-added-state">
                                  ${selectedQuantity} currently in your enrollment
                              </div>`
                            : nothing
                    }
                    <div class="product-actions" part="product-actions">
                        <button
                            class="product-action"
                            part="product-action primary-action"
                            type="button"
                            ?disabled=${this.busy || !offer}
                            @click=${() => this.addProduct(product, purchaseType)}
                        >
                            ${
                                selectedQuantity > 0
                                    ? "Add another"
                                    : purchaseType === "recurring"
                                      ? "Add to recurring"
                                      : "Add to enrollment"
                            }
                        </button>
                    </div>
                </div>
            </div>
        </article>`;
    }
    private renderCardContent(product: CommerceProductSummary) {
        const content = product.cardContent ?? [];
        return content.length
            ? html`<div class="card-content" part="product-card-content">
                  ${content.map(
                      (line) =>
                          html`<div class="card-content-line" part="product-card-content-line">
                              ${
                                  line.label === undefined
                                      ? nothing
                                      : html`<span class="card-content-label">${line.label}</span>`
                              }${
                                  line.format === "rich-html"
                                      ? html`<div class="card-content-rich" part="rich-content">
                                            ${unsafeHTML(
                                                sanitizeCommerceHtml(line.value, {
                                                    allowLinks: false,
                                                }),
                                            )}
                                        </div>`
                                      : html`<span>${line.value}</span>`
                              }
                          </div>`,
                  )}
              </div>`
            : nothing;
    }
    private renderTools(kits: boolean) {
        return html`<div class="tools" part="toolbar">
            <input
                class="search"
                part="search"
                type="search"
                aria-label=${kits ? "Search enrollment options" : "Search products"}
                placeholder=${kits ? "Search enrollment options" : "Search products"}
                .value=${this.query}
                @input=${this.searchChanged}
            /><select
                class="sort"
                part="sort"
                aria-label="Sort results"
                .value=${this.sort}
                @change=${this.sortChanged}
            >
                <option value="default">Recommended</option>
                <option value="name-ascending">Name: A–Z</option>
                <option value="name-descending">Name: Z–A</option>
                <option value="price-ascending">Price: low to high</option>
                <option value="price-descending">Price: high to low</option>
            </select>
        </div>`;
    }
    private renderCategoryChips(values: readonly CommerceProductCategory[]) {
        return values.length
            ? html`<div class="categories" part="categories" aria-label="Product categories">
                  <button
                      class="chip ${this.categoryId ? "" : "active"}"
                      part="category"
                      @click=${() => this.chooseCategory("")}
                  >
                      All</button
                  >${values.map(
                      (value) =>
                          html`<button
                              class="chip ${this.categoryId === value.id ? "active" : ""}"
                              part="category"
                              @click=${() => this.chooseCategory(value.id)}
                          >
                              ${value.name}
                          </button>`,
                  )}
              </div>`
            : nothing;
    }
    private renderCart() {
        const cart = this.enrollment!.cart;
        return html`<aside class="summary" part="summary">
            <div class="eyebrow" part="eyebrow">Enrollment summary</div>
            ${
                cart.lines.length
                    ? cart.lines.map(
                          (value) =>
                              html`<div class="line" part="summary-line">
                                  <span
                                      >${value.line.name} × ${value.line.quantity}<small
                                          class="lock"
                                          >${
                                              value.quantityLocked
                                                  ? "🔒 Included and fixed"
                                                  : value.line.purchaseType === "recurring"
                                                    ? "Recurring"
                                                    : "Optional"
                                          }</small
                                      ></span
                                  ><strong>${value.line.lineTotal.formatted}</strong>
                              </div>`,
                      )
                    : html`<p>Your selected collection will appear here.</p>`
            }
            <div class="line" part="summary-total">
                <strong>Total</strong><strong>${cart.totals.total.formatted}</strong>
            </div>
            ${this.renderSummaryContinue()}
        </aside>`;
    }
    private stageAction() {
        const stage = this.enrollment!.currentStage;
        const primary =
            stage.allowedOutcomes.find((value) => value !== "skip") ?? stage.allowedOutcomes[0];
        const expectedPurchaseType = stage.kind === "recurring-offer" ? "recurring" : "one-time";
        const hasCurrentStageSelection = this.enrollment!.cart.lines.some(
            (value) =>
                value.role === "optional" && value.line.purchaseType === expectedPurchaseType,
        );
        const useSkip = stage.skippable && !hasCurrentStageSelection;
        return {
            outcome: useSkip && stage.allowedOutcomes.includes("skip") ? "skip" : primary,
            label: useSkip
                ? (stage.presentation.secondaryActionLabel ?? "Not now")
                : stage.presentation.primaryActionLabel,
        };
    }
    private renderSummaryContinue() {
        const action = this.stageAction();
        return action.outcome
            ? html`<div class="summary-continue" part="summary-continue">
                  <p>Your selections are saved. Continue whenever you are ready.</p>
                  <button
                      class="action"
                      part="summary-primary-action primary-action"
                      ?disabled=${this.busy}
                      @click=${() => this.complete(action.outcome!)}
                  >
                      ${action.label} →
                  </button>
              </div>`
            : nothing;
    }
    private renderActions() {
        const action = this.stageAction();
        return html`<div class="actions" part="actions">
            <button class="action secondary" part="secondary-action" @click=${this.abandon}>
                Abandon enrollment
            </button>
            <div>
                ${
                    action.outcome
                        ? html`<button
                              class="action"
                              part="primary-action"
                              ?disabled=${this.busy}
                              @click=${() => this.complete(action.outcome!)}
                          >
                              ${action.label} →
                          </button>`
                        : nothing
                }
            </div>
        </div>`;
    }
    private renderProductDialog() {
        const product = this.detailProduct;
        const summary = this.detailSummary;
        if (!summary) return nothing;
        const images = this.detailImages(product, summary);
        const image = images[this.detailActiveImageIndex] ?? images[0] ?? null;
        return html`<dialog
            class="product-dialog"
            part="product-dialog"
            aria-label=${`${summary.name} details`}
            @close=${this.productDetailsClosed}
        >
            <button
                class="dialog-close"
                aria-label="Close product details"
                @click=${this.closeProductDetails}
            >
                ×
            </button>
            <div class="dialog-layout">
                <div class="dialog-gallery" part="product-dialog-gallery">
                    <div
                        class="dialog-media"
                        part="product-dialog-media"
                        @pointerdown=${this.detailImagePointerDown}
                        @pointerup=${(event: PointerEvent) =>
                            this.detailImagePointerUp(event, images.length)}
                        @pointercancel=${this.cancelDetailImageSwipe}
                    >
                        ${
                            image
                                ? html`<img
                                      src=${image.url}
                                      alt=${image.alternativeText}
                                      draggable="false"
                                  />`
                                : html`<div class="placeholder">✦</div>`
                        }
                    </div>
                    ${
                        images.length > 1
                            ? html`<div class="dialog-thumbnails" part="product-dialog-thumbnails">
                                      ${images.map(
                                          (candidate, index) =>
                                              html`<button
                                                  class="dialog-thumbnail ${
                                                      index === this.detailActiveImageIndex
                                                          ? "active"
                                                          : ""
                                                  }"
                                                  part="product-dialog-thumbnail"
                                                  type="button"
                                                  aria-label=${`View ${candidate.alternativeText.trim() || summary.name} image ${index + 1}`}
                                                  aria-pressed=${
                                                      index === this.detailActiveImageIndex
                                                          ? "true"
                                                          : "false"
                                                  }
                                                  @click=${() =>
                                                      (this.detailActiveImageIndex = index)}
                                              >
                                                  <img src=${candidate.url} alt="" loading="lazy" />
                                              </button>`,
                                      )}
                                  </div>
                                  <div
                                      class="dialog-mobile-gallery-controls"
                                      part="product-dialog-mobile-gallery-controls"
                                      aria-label="Product image controls"
                                  >
                                      <button
                                          class="dialog-gallery-navigation"
                                          part="product-dialog-gallery-previous"
                                          type="button"
                                          aria-label="View previous product image"
                                          @click=${() => this.selectRelativeDetailImage(-1, images.length)}
                                      >
                                          ←
                                      </button>
                                      <span
                                          class="dialog-gallery-position"
                                          role="status"
                                          aria-live="polite"
                                      >
                                          Image ${this.detailActiveImageIndex + 1} of
                                          ${images.length}
                                      </span>
                                      <button
                                          class="dialog-gallery-navigation"
                                          part="product-dialog-gallery-next"
                                          type="button"
                                          aria-label="View next product image"
                                          @click=${() => this.selectRelativeDetailImage(1, images.length)}
                                      >
                                          →
                                      </button>
                                  </div>`
                            : nothing
                    }
                </div>
                <div class="dialog-copy" part="product-dialog-content">
                    <span class="badge">Product details</span>
                    <h2>${summary.name}</h2>
                    ${
                        this.detailLoading
                            ? html`<p role="status">Loading product details…</p>`
                            : this.detailError
                              ? html`<p class="notice" role="alert">${this.detailError}</p>`
                              : html`${
                                    product?.description || summary.shortDescription
                                        ? html`<p class="dialog-description">
                                              ${product?.description || summary.shortDescription}
                                          </p>`
                                        : nothing
                                }
                                ${
                                    product === undefined
                                        ? nothing
                                        : this.renderProductDetailSections(product)
                                }`
                    }
                </div>
            </div>
        </dialog>`;
    }

    private renderProductDetailSections(product: CommerceProduct) {
        const sections = product.detailSections ?? [];
        if (sections.length === 0) return nothing;

        return html`<div class="dialog-detail-sections" part="product-detail-sections">
            ${sections.map((section) => {
                const content =
                    section.format === "rich-html"
                        ? html`<div class="dialog-detail-content" part="rich-content">
                              ${unsafeHTML(sanitizeCommerceHtml(section.content))}
                          </div>`
                        : html`<div class="dialog-detail-content">${section.content}</div>`;

                return section.display === "accordion"
                    ? html`<details
                          class="dialog-detail-section"
                          part="product-detail-section product-detail-accordion"
                      >
                          <summary>${section.title ?? "More information"}</summary>
                          ${content}
                      </details>`
                    : html`<section
                          class="dialog-detail-section"
                          part="product-detail-section product-detail-body"
                      >
                          ${section.title === undefined ? nothing : html`<h3>${section.title}</h3>`}
                          ${content}
                      </section>`;
            })}
        </div>`;
    }

    private bind() {
        this.unsubscribe?.();
        if (!this.commerce) return;
        this.enrollment = this.commerce.enrollment.current ?? undefined;
        this.queueStageRouteRequest();
        this.unsubscribe = this.commerce.enrollment.onChanged((event) => {
            const previousStageId = this.enrollment?.currentStage.id;
            this.enrollment = event.enrollment ?? undefined;
            const stageChanged =
                previousStageId !== undefined &&
                this.enrollment !== undefined &&
                previousStageId !== this.enrollment.currentStage.id;
            this.query = "";
            this.categoryId = "";
            const productsLoaded = this.loadProducts();
            this.queueStageRouteRequest();
            this.dispatchEvent(
                new CustomEvent(`teqtank-enrollment-${event.reason}`, {
                    detail: event,
                    bubbles: true,
                    composed: true,
                }),
            );
            if (stageChanged) {
                this.scrollJourneyIntoView("smooth");
                void this.finishStageScroll(productsLoaded);
            }
        });
        if (this.initializedCommerce !== this.commerce) {
            this.initializedCommerce = this.commerce;
            void this.initialize();
        }
    }
    private async initialize() {
        await this.run(async () => {
            await this.require().enrollment.resume();
        });
    }
    private selectKit = (id: string) =>
        this.run(async () => {
            await this.require().enrollment.selectKit(id);
        }, true);
    private complete = (outcome: string) =>
        this.run(async () => {
            await this.require().enrollment.completeStage(outcome);
        }, true);
    private abandon = () =>
        this.run(async () => {
            await this.require().enrollment.abandon();
        });
    private readonly continueEnrollmentOnCurrentPage = (event: Event): void => {
        event.preventDefault();
        this.queueStageRouteRequest(true);
    };
    private readonly checkoutSubmitted = (): void => {
        this.awaitingCheckoutContinuation = true;
    };
    private readonly continueAfterCheckout = (event: CustomEvent<{ route: string }>): void => {
        if (event.detail.route) return;
        event.preventDefault();
        this.awaitingCheckoutContinuation = false;
        this.queueStageRouteRequest(true);
    };
    private readonly requestCurrentStageRoute = (event: Event): void => {
        const enrollment = this.enrollment;
        if (!enrollment) return;
        const route = this.currentStageRoute();
        if (!route) return;
        event.preventDefault();
        this.requestStageRoute(enrollment, route, true);
    };
    private currentStageRoute(): string {
        const stage = this.enrollment?.currentStage;
        if (!stage) return "";
        return (
            resolveEnrollmentStageRoute(this.stageRoutes, stage) ||
            (stage.kind === "checkout" ? this.checkoutRoute.trim() : "")
        );
    }
    private hasPendingExternalStageRoute(): boolean {
        const route = this.currentStageRoute();
        return route.length > 0 && !isCurrentBrowserRoute(route);
    }
    private queueStageRouteRequest(force = false): void {
        queueMicrotask(() => {
            if (this.awaitingCheckoutContinuation) return;
            const enrollment = this.enrollment;
            if (!enrollment) return;
            const route = this.currentStageRoute();
            if (!route || isCurrentBrowserRoute(route)) return;
            this.requestStageRoute(enrollment, route, force);
        });
    }
    private requestStageRoute(
        enrollment: CommerceEnrollmentSession,
        route: string,
        force: boolean,
    ): void {
        const stage = enrollment.currentStage;
        const key = `${enrollment.id}:${enrollment.version}:${stage.id}:${route}`;
        if (!force && this.requestedRouteKey === key) return;
        this.requestedRouteKey = key;
        const navigate = this.dispatchEvent(
            new CustomEvent("teqtank-enrollment-route-requested", {
                detail: {
                    route,
                    enrollmentId: enrollment.id,
                    stageId: stage.id,
                    stageKind: stage.kind,
                },
                bubbles: true,
                composed: true,
                cancelable: true,
            }),
        );
        if (navigate && typeof window !== "undefined") {
            window.location.assign(new URL(route, window.location.href).toString());
        }
    }
    private async addProduct(
        product: CommerceProductSummary,
        purchaseType: CommerceCartPurchaseType,
    ) {
        await this.run(async () => {
            const catalogueId = this.enrollment?.currentStage.catalogueId;
            const detail = catalogueId
                ? await this.require().products.getByIdInCatalogue(
                      catalogueId,
                      product.id,
                      "enrollment",
                  )
                : await this.require().products.getById(product.id);
            const variant =
                detail.variants.find((value) => value.id === detail.defaultVariantId) ??
                detail.variants[0];
            const offer = variant?.offers.find((value) => value.purchaseType === purchaseType);
            if (!variant || !offer) throw new Error("That purchase option is no longer available.");
            const frequency = offer.frequency?.options.find(
                (value) => value.id === offer.frequency?.defaultOptionId,
            );
            await this.require().enrollment.addProduct({
                productId: product.id,
                variantId: variant.id,
                quantity: 1,
                purchaseType,
                ...(frequency
                    ? {
                          frequencyId: frequency.id,
                          frequencyDays: frequency.days,
                      }
                    : {}),
            });
        });
    }
    private async openProductDetails(product: CommerceProductSummary): Promise<void> {
        this.detailSummary = product;
        this.detailProduct = undefined;
        this.detailError = "";
        this.detailLoading = true;
        this.detailActiveImageIndex = 0;
        await this.updateComplete;
        if (!this.productDialog.open) this.productDialog.showModal();

        try {
            const catalogueId = this.enrollment?.currentStage.catalogueId;
            this.detailProduct = catalogueId
                ? await this.require().products.getByIdInCatalogue(
                      catalogueId,
                      product.id,
                      "enrollment",
                  )
                : await this.require().products.getById(product.id);
        } catch (error) {
            this.detailError =
                error instanceof Error ? error.message : "Product details could not be loaded.";
        } finally {
            this.detailLoading = false;
        }
    }
    private readonly closeProductDetails = (): void => {
        this.productDialog.close();
    };
    private readonly productDetailsClosed = (): void => {
        this.detailProduct = undefined;
        this.detailSummary = undefined;
        this.detailLoading = false;
        this.detailError = "";
        this.detailActiveImageIndex = 0;
        this.detailImagePointerStartX = undefined;
    };

    private detailImages(
        product: CommerceProduct | undefined,
        summary: CommerceProductSummary,
    ): readonly CommerceImage[] {
        const fallback = product?.primaryImage ?? summary.primaryImage;
        const candidates = [...(product?.images ?? []), fallback];
        const images: CommerceImage[] = [];
        const seenUrls = new Set<string>();

        for (const candidate of candidates) {
            if (
                candidate === null ||
                candidate === undefined ||
                candidate.url.trim().length === 0
            ) {
                continue;
            }

            try {
                const url = new URL(candidate.url.trim());
                if (url.protocol !== "https:" && url.protocol !== "http:") continue;
            } catch {
                continue;
            }

            const normalizedUrl = candidate.url.trim();
            const identity = normalizedUrl.toLowerCase();
            if (seenUrls.has(identity)) continue;

            seenUrls.add(identity);
            images.push({ ...candidate, url: normalizedUrl });
        }

        return images;
    }

    private selectRelativeDetailImage(direction: -1 | 1, imageCount: number): void {
        if (imageCount < 2) return;

        this.detailActiveImageIndex =
            (this.detailActiveImageIndex + direction + imageCount) % imageCount;
    }

    private readonly detailImagePointerDown = (event: PointerEvent): void => {
        this.detailImagePointerStartX = event.clientX;
    };

    private detailImagePointerUp(event: PointerEvent, imageCount: number): void {
        const startX = this.detailImagePointerStartX;
        this.detailImagePointerStartX = undefined;

        if (startX === undefined || imageCount < 2) return;

        const distance = event.clientX - startX;
        if (Math.abs(distance) < 35) return;

        this.selectRelativeDetailImage(distance > 0 ? -1 : 1, imageCount);
    }

    private readonly cancelDetailImageSwipe = (): void => {
        this.detailImagePointerStartX = undefined;
    };
    private searchChanged = (event: InputEvent) => {
        this.query = (event.currentTarget as HTMLInputElement).value;
        if (this.enrollment?.currentStage.kind !== "kit-selection") {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => void this.loadProducts(), 350);
        }
    };
    private sortChanged = (event: Event) => {
        this.sort = (event.currentTarget as HTMLSelectElement).value as CommerceProductSort;
        if (this.enrollment?.currentStage.kind !== "kit-selection") void this.loadProducts();
    };
    private chooseCategory(id: string) {
        this.categoryId = id;
        if (this.enrollment?.currentStage.kind !== "kit-selection") void this.loadProducts();
    }
    private filteredKits() {
        const query = this.query.trim().toLocaleLowerCase();
        let values = this.enrollment!.kits.filter(
            (kit) =>
                (!query ||
                    `${kit.product.name} ${kit.product.shortDescription} ${kit.highlights.join(" ")}`
                        .toLocaleLowerCase()
                        .includes(query)) &&
                (!this.categoryId || kit.product.categoryIds.includes(this.categoryId)),
        );
        const price = (kit: CommerceEnrollmentKit) =>
            kit.product.representativeOffers[0]?.price.current.amount ?? 0;
        values = [...values].sort((a, b) =>
            this.sort === "name-ascending"
                ? a.product.name.localeCompare(b.product.name)
                : this.sort === "name-descending"
                  ? b.product.name.localeCompare(a.product.name)
                  : this.sort === "price-ascending"
                    ? price(a) - price(b)
                    : this.sort === "price-descending"
                      ? price(b) - price(a)
                      : 0,
        );
        return values;
    }
    private async loadProducts() {
        const commerce = this.commerce;
        const kind = this.enrollment?.currentStage.kind;
        if (!commerce || (kind !== "catalogue" && kind !== "recurring-offer")) return;
        this.loadingProducts = true;
        this.error = "";
        try {
            const [products, categories] = await Promise.all([
                this.searchAllProducts(commerce, kind === "recurring-offer"),
                this.loadCategories(commerce),
            ]);
            this.products = products;
            this.categories = categories;
        } catch (error) {
            this.error = error instanceof Error ? error.message : "Products could not be loaded.";
            this.products = [];
        } finally {
            this.loadingProducts = false;
        }
    }
    private async searchAllProducts(commerce: Commerce, recurringOnly: boolean) {
        const query = this.query.trim();
        const catalogueId = this.enrollment?.currentStage.catalogueId ?? undefined;
        const items: CommerceProductSummary[] = [];
        let page = 1;
        let hasNext = true;
        while (hasNext) {
            const result = await commerce.products.search({
                experience: "enrollment",
                ...(catalogueId ? { catalogueId } : {}),
                ...(query ? { query } : {}),
                ...(this.categoryId ? { categoryId: this.categoryId } : {}),
                sort: this.sort,
                page,
                pageSize: 100,
                ...(recurringOnly ? { recurringAvailable: true } : {}),
            });
            items.push(...result.items);
            hasNext = result.pagination.hasNextPage;
            page += 1;
        }
        return items;
    }
    private async loadCategories(commerce: Commerce) {
        const catalogueId = this.enrollment?.currentStage.catalogueId ?? undefined;
        const collected: CommerceProductCategory[] = [];
        const pending: (string | null | undefined)[] = [undefined];
        const seen = new Set<string>();
        while (pending.length) {
            const parentId = pending.shift();
            const values = catalogueId
                ? await commerce.products.getCategoriesInCatalogue(
                      catalogueId,
                      "enrollment",
                      parentId,
                  )
                : await commerce.products.getCategories(parentId);
            for (const value of values) {
                if (seen.has(value.id)) continue;
                seen.add(value.id);
                collected.push(value);
                pending.push(value.id);
            }
        }
        return collected.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    }
    private async finishStageScroll(productsLoaded: Promise<void>): Promise<void> {
        await this.updateComplete;
        await productsLoaded;
        await this.updateComplete;
        this.scrollJourneyIntoView("smooth");
    }
    private scrollJourneyIntoView(behavior: ScrollBehavior): void {
        if (this.disableStageScroll || !this.isConnected || !this.scrollIntoView) return;
        const reducedMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        this.scrollIntoView({
            behavior: reducedMotion ? "auto" : behavior,
            block: "start",
            inline: "nearest",
        });
    }
    private async run(action: () => Promise<unknown>, scrollForStageChange = false) {
        if (scrollForStageChange) this.scrollJourneyIntoView("smooth");
        this.busy = true;
        this.error = "";
        try {
            await action();
        } catch (error) {
            this.error =
                error instanceof Error ? error.message : "Enrollment could not be updated.";
        } finally {
            this.busy = false;
        }
    }
    private require() {
        if (!this.commerce) throw new Error("Commerce runtime is unavailable.");
        return this.commerce;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-enrollment": TeqtankEnrollment;
    }
}
